import { useEffect, useState, useCallback } from 'react'
import { io } from 'socket.io-client'

let socketInstance = null

/**
 * Hook para manejar notificaciones en tiempo real
 * Se conecta automáticamente a Socket.io y maneja eventos de notificaciones
 */
export function useNotifications(userId) {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)

  // Conectar a Socket.io
  useEffect(() => {
    if (!userId) return

    // Crear conexión si no existe
    if (!socketInstance) {
      // En producción usar URL relativa (pasa por el proxy Apache/Nginx)
      // En desarrollo usar VITE_API_BASE
      let apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:4000'
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        // En producción, usar URL relativa sin puerto - pasa por el proxy del servidor
        apiBase = ''
      }
      
      console.log('[SOCKET] Connecting to:', apiBase || window.location.origin)
      
      socketInstance = io(apiBase, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ['websocket', 'polling'],
        path: '/socket.io'
      })

      // Listener para conexión
      socketInstance.on('connect', () => {
        console.log('[SOCKET] Connected:', socketInstance.id)
        setIsConnected(true)

        // Autenticar con el servidor
        socketInstance.emit('authenticate', userId)
      })

      // Listener para desconexión
      socketInstance.on('disconnect', () => {
        console.log('[SOCKET] Disconnected')
        setIsConnected(false)
      })

      // Listener para nuevas solicitudes
      socketInstance.on('request:created', (data) => {
        console.log('[NOTIFICATION] New request:', data)
        const notification = {
          id: `${Date.now()}-${Math.random()}`,
          type: data.type,
          title: data.title,
          message: data.message,
          timestamp: data.timestamp,
          read: false,
          data: data
        }
        setNotifications(prev => [notification, ...prev])
        setUnreadCount(prev => prev + 1)

        // Reproducir sonido si está disponible
        playNotificationSound()
      })

      // Listener para cambios de estado
      socketInstance.on('request:status-changed', (data) => {
        console.log('[NOTIFICATION] Status changed:', data)
        const notification = {
          id: `${Date.now()}-${Math.random()}`,
          type: 'status_change',
          title: data.title || '🔄 Estado Actualizado',
          message: data.message,
          timestamp: data.timestamp,
          read: false,
          data: data
        }
        setNotifications(prev => [notification, ...prev])
        setUnreadCount(prev => prev + 1)
        playNotificationSound()
      })
    }

    return () => {
      // No desconectar aquí, mantener la conexión activa
    }
  }, [userId])

  // Marcar notificación como leída
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    )
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  // Marcar todas como leídas
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    setUnreadCount(0)
  }, [])

  // Limpiar notificación
  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId))
  }, [])

  // Limpiar todas las notificaciones
  const clearAllNotifications = useCallback(() => {
    setNotifications([])
    setUnreadCount(0)
  }, [])

  return {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications
  }
}

// Reproducir sonido de notificación
function playNotificationSound() {
  try {
    // Usar una notificación de sistema si está disponible
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAAB9AAACABAAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj==')
    audio.play().catch(() => {
      // Ignorar error si no puede reproducir
    })
  } catch (e) {
    // Ignorar errores
  }
}
